import React from 'react';
import "./smile-pages.css";

export default function WhatHappensToYourBrainWhenYouSmile() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">
      <h1>What Happens to Your Brain When You Smile?</h1>
      <p>
        When you <strong>smile</strong>—especially a genuine <strong>smile</strong>—your brain undergoes a
        series of powerful changes that affect your <strong>mood</strong>, <strong>stress levels</strong>, and even your
        <strong> physical health</strong>. Here's a breakdown of what happens in your brain when you smile,
        supported by neuroscience and psychological research:
      </p>

      <hr />

      <h3>🧠 What Happens in the Brain When You Smile</h3>

      <h4>1. Activation of the Reward System</h4>
      <ul>
        <li>
          Smiling triggers activity in the <strong>brain's reward centers</strong>, particularly in areas like the
          <strong> nucleus accumbens</strong> and <strong>prefrontal cortex</strong>.
        </li>
        <li>This leads to the release of neurotransmitters that make you feel good.</li>
      </ul>

      <h4>✅ Neurotransmitters Released</h4>
      <ul>
        <li><strong>Dopamine</strong> – boosts feelings of pleasure and motivation</li>
        <li><strong>Serotonin</strong> – stabilizes mood and reduces anxiety</li>
        <li><strong>Endorphins</strong> – act as natural painkillers and mood elevators</li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Niedenthal, P. M. (2007). <em>Embodied emotion</em>. Current Directions in Psychological Science.
        </li>
      </ul>

      <hr />

      <h4>2. Facial Feedback Loop</h4>
      <ul>
        <li>
          The <strong>Facial Feedback Hypothesis</strong> suggests that facial expressions like smiling send signals
          back to the brain, reinforcing the emotion you're expressing.
        </li>
        <li>
          So even a <strong>forced smile</strong> can improve your mood because smiling “tricks” your brain into
          thinking you're happy.
        </li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Strack, F., Martin, L. L., & Stepper, S. (1988). <em>
            Inhibiting and facilitating conditions of the human smile: A nonobtrusive test of the facial
            feedback hypothesis
          </em>. Journal of Personality and Social Psychology.
        </li>
      </ul>

      <hr />

      <h4>3. Reduction of Stress Hormones</h4>
      <ul>
        <li>
          Smiling reduces <strong>cortisol</strong>, the stress hormone, helping calm your nervous system and lower
          physical tension.
        </li>
        <li>
          It also increases <strong>parasympathetic nervous system</strong> activity (the “rest-and-digest” mode),
          leading to a slower heart rate and deeper relaxation.
        </li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Kraft & Pressman (2012). <em>
            Grin and bear it: The influence of manipulated facial expression on the stress response
          </em>. Psychological Science.
        </li>
      </ul>

      <hr />

      <h4>4. Improved Emotional Processing</h4>
      <ul>
        <li>
          Smiling lights up the <strong>amygdala</strong> (emotion center) and improves the brain’s ability to
          <strong> regulate emotions</strong>.
        </li>
        <li>This helps you respond to challenges more calmly and with greater emotional resilience.</li>
      </ul>

      <hr />

      <h3>🧠 Brain Areas Involved in Smiling</h3>

      <table className="table mt-3">
        <thead>
          <tr>
            <th>Brain Region</th>
            <th>Role</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Motor cortex</td>
            <td>Controls the muscles involved in facial expressions</td>
          </tr>
          <tr>
            <td>Prefrontal cortex</td>
            <td>Interprets emotions, decision-making, positive mood</td>
          </tr>
          <tr>
            <td>Nucleus accumbens</td>
            <td>Central to the brain’s reward system</td>
          </tr>
          <tr>
            <td>Amygdala</td>
            <td>Processes emotions, including joy and fear</td>
          </tr>
          <tr>
            <td>Hypothalamus</td>
            <td>Regulates stress response and hormone release</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h3>🔑 Keys to Remember</h3>
      <p>
        When you smile, your brain releases <strong>feel-good chemicals</strong>, lowers stress, and activates emotional
        centers that boost your mood and well-being—even if the smile is forced. Smiling is a fast, natural way to
        create a positive shift in both your brain and your body.
      </p>
      <p>
        <strong>That Smile App is here to help you improve your happiness and health through smiling more.</strong>
      </p>
    </div>
  );
}
